<template>
    <div>
        <div class="w-11/12 m-auto flex items-end justify-between mb-12 pt-8">
            <a :href="`questionnaire?q=${questionnaire}`" class="text-base sm:text-xl md:text-2xl bg-white hover:bg-lightGreen-300 text-lightGreen-300 hover:text-white px-3 md:px-6 py-1 md:py-2 rounded-full cursor-pointer">Restart test</a>
            <a href="/" >
                <img src="assets/img/nesst-logo.svg" alt="NESst-logo" class="w-20 md:w-32 py-1 md:py-0 text-center m-auto">
            </a>
            <div class="text-base sm:text-xl md:text-2xl bg-lightGreen-300 text-white px-4 md:px-8 py-1 md:py-2 rounded-full"
                 :class="{invisible: total === 0}">
                <span class="font-bold">{{ answered }}</span>/{{ total }}
            </div>
        </div>

        <div v-if="error" class="questionnaire-wrapper w-6/12 mx-auto flex flex-col flex-1 justify-center bg-white p-4 sm:p-6 md:px-10">
            <h3 class="relative text-xl sm:text-2xl lg:text-3xl leading-tight md:leading-normal font-bold text-muted-500 text-center">{{ error }}</h3>
        </div>
        <loader v-else-if="loading" class="mx-auto my-64"/>
        <div v-else class="w-10/12 xl:w-2/3 xxl:w-1/2 m-auto flex flex-wrap justify-center">
            <div v-for="competency in all_competencies" :key="competency.id"
                    class="w-full sm:w-1/2 md:w-2/5 lg:w-1/4 mb-6 md:mb-8 sm:px-2">
                <div @click="openFeedbackModal(competency)"
                     class="relative h-full bg-white rounded-lg shadow flex flex-col justify-between items-center text-center cursor-pointer p-3">
                        <svg @click.stop="openNotesModal(competency)"
                             class="absolute right-0 top-0 mt-2 mr-2 fill-current text-darkGreen-200 hover:text-darkGreen-100"
                             xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M6 22v-16h16v7.543c0 4.107-6 2.457-6 2.457s1.518 6-2.638 6h-7.362zm18-7.614v-10.386h-20v20h10.189c3.163 0 9.811-7.223 9.811-9.614zm-10 1.614h-5v-1h5v1zm5-4h-10v1h10v-1zm0-3h-10v1h10v-1zm3-6h-19v19h-1v-20h20v1zm-2-2h-19v19h-1v-20h20v1z"/></svg>
                        <img :src="getCompetencyIcon(competency)" :alt="competency.name | trans" class="w-20 py-6 text-center m-auto">
                    <h4 class="text-base text-black font-bold">{{ competency.name | trans }}</h4>
                    <score-bar :score="getCompetencyScore(competency)" class="mt-6"/>
                </div>
            </div>
        </div>

        <score-feedback-modal
                v-if="feedbackModal"
                :title="modal.title"
                :description="modal.description"
                :icon="modal.icon"
                :score="modal.score"
                :slides="modal.slides"
                @close="feedbackModal = false" />

        <score-share-modal
                v-if="shareModal"
                :shareToken="shareToken"
                :questionnaire="questionnaire"
                @close="shareModal = false" />

        <score-note-modal
                v-if="noteModal"
                :notes="notesForModal"
                :shareToken="shareToken"
                :questionnaire="questionnaire"
                :session="session"
                :competency="competency"
                :can_add_notes="can_add_notes"
                :isLoggedIn="isLoggedIn"
                :returnUrl="returnUrl"
                @close="noteModal = false" />
    </div>
</template>

<script>
    import { firebase } from '../../firebase';
    import { getScoreAndFeedback } from '../../questions/score-calculator';
    import Loader from '../shared/Loader';
    import ScoreBar from '../shared/ScoreBar';
    import ScoreFeedbackModal from '../shared/ScoreFeedbackModal';
    import ScoreShareModal from '../shared/ScoreShareModal';
    import ScoreNoteModal from '../shared/ScoreNoteModal';
    import Answers from '../../data/answers';
    import Questionnaire from '../../data/questionnaire';

    export default {
        components: {
            ScoreShareModal,
            ScoreFeedbackModal,
            ScoreNoteModal,
            ScoreBar,
            Loader
        },

        data() {
            const questionnaireStorage = new Questionnaire(this);

            return {
                loading: true,
                feedbackModal: false,
                shareModal: false,
                noteModal: false,
                questionnaireStorage,
                questionnaire: questionnaireStorage.get(),
                error: null,
                all_competencies: {},
                all_feedback: {},
                competencies: {},
                competency: null,
                shareToken: null,
                session: null,
                sessions: {},
                isLoggedIn: false,
                can_add_notes: null,
                notes: {},
                notesForModal: [],
                modal: {
                    title: '',
                    description: '',
                    icon: '',
                    score: 0,
                    slides: [],
                },
                returnUrl: null
            }
        },

        computed: {
            answered() {
                return this.session ? this.sessions[this.session].numberOfAnswers : 0;
            },
            total() {
                return this.session ? this.sessions[this.session].numberOfQuestions : 0;
            },
        },

        methods: {
            openFeedbackModal(competency) {
                this.modal.title = this.$options.filters.trans(competency.name);
                this.modal.description = this.$options.filters.trans(competency.description);
                this.modal.icon = this.getCompetencyIcon(competency);
                this.modal.score = this.getCompetencyScore(competency);
                this.modal.slides = this.getFeedbackForCompetency(competency);
                this.feedbackModal = true;
            },
            getCompetencyScore(competency) {
                return this.sessions[this.session].score[competency.id] || 0;
            },
            getCompetencyIcon(competency) {
                let name = competency.name.en.replace('/', '-');
                return `assets/img/competencies/${name}.svg`;
            },
            getFeedbackForCompetency(competency) {
                const feedback = [];

                (this.sessions[this.session].feedback[competency.id] || []).forEach(key => {
                    const fb = this.$options.filters.trans(this.all_feedback[key]);
                    feedback.push(fb);
                });

                return feedback;
            },
            openNotesModal(competency) {
                const questionnaireNotes = this.notes[this.questionnaire] || {};
                const sessionNotes = questionnaireNotes[this.session] || {};
                this.notesForModal = sessionNotes[competency.id] || [];
                this.competency = competency;
                this.noteModal = true;
            },
        },

        mounted() {
            this.shareToken = this.$route.query.token || null;

            const answers = new Answers();
            answers.removeAll();

            if (!this.shareToken) {
                return this.error = 'Missing share token!';
            }

            if (!this.questionnaire) {
                return this.error = 'Missing questionnaire!';
            }

            let session = this.$route.query.s || null;
            session = session === 'undefined' ? null : session;

            getScoreAndFeedback(this.shareToken, this.questionnaire, session).then(({data}) => {
                Object.keys(data).forEach(key => this[key] = data[key]);

                if (data.error) {
                    return;
                }
                
                this.can_add_notes = data.can_add_notes;

                if (!session || typeof data.sessions[session] === 'undefined') {
                    const sessions = Object.keys(data.sessions).sort((a, b) => Number(b) - Number(a));
                    session = sessions.slice(-1).pop();
                    this.$router.replace({query: {
                        token: this.shareToken,
                        q: this.questionnaire,
                        s: session,
                    }})
                }

                this.session = session;
                this.returnUrl = encodeURIComponent(`scores?token=${this.shareToken}&q=${this.questionnaire}&s=${this.session}`);
                this.loading = false;
            });

            firebase.auth().onAuthStateChanged(user => {
                if (!user) {
                    return
                }
                this.isLoggedIn = true;
            });
        }
    }
</script>
